import { useCallback, useEffect, useState } from 'react';
import { Notification } from '../utils';
import loginService from '../Services/login.service';
import accessTokenService from '../Services/access-token.service';
import registerService from '../Services/register.service';
import useTimer from './timer.hook';
import useDocumentTitle from './document-title.hook';
import { useSearchParams } from 'react-router-dom';

const HOME_PAGE_ROUTE = process.env.REACT_APP_PUBLIC_HOME_PAGE || '';

export interface ILoginForm {
  username: string;
  password: string;
  rememberMe: boolean;
}

interface IState {
  loading?: boolean;
  dest: string;
  email?: string;
}

const useLogin = () => {
  useDocumentTitle('Sign In')
  const [searchParams, setSearchParams] = useSearchParams();
  const { email, dest, msg, error, get_out }: Record<string, string | undefined> = Object.fromEntries(searchParams.entries());
  const [state, setState] = useState<IState>({
    dest: dest || HOME_PAGE_ROUTE,
    email
  });
  const [captchaToken, setCaptchaToken] = useState('');
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);
  const [resendTimer, resetResendTimer] = useTimer(60);

  const updateState = (newStateOrFn: Partial<IState>) => (
    setState(prevState => ({
      ...prevState,
      ...newStateOrFn
    }))
  );

  const captchaVerifyHandler = useCallback(
    (token: string) => {
      setCaptchaToken(token);
    },
    [setCaptchaToken],
  );

  const redirectWithToken = (dest: string, accessToken: string) => {
    try {
      const redirectUrl = new URL(dest);
      redirectUrl.searchParams.set('access_token', accessToken);
      window.location.href = redirectUrl.toString();
    } catch {
      window.location.href = dest;
    }
  };

  const loginHandler = async (values: ILoginForm) => {
    if (!captchaToken) {
      setRefreshReCaptcha((r) => !r);
      return;
    }

    try {
      updateState({ loading: true });

      const data = await loginService.login(values, captchaToken);
      const accessToken = data.result?.accessToken;

      if (data.type === 'error' || !accessToken) {
        const backendMessage = data.result?.message;

        let errorMessage = 'An error occurred while logging you in.';

        if (backendMessage) {
          errorMessage = backendMessage;
        } else if (data.status === 401 || data.status === 403) {
          errorMessage = 'Your username or password is incorrect.';
        }

        Notification.error({
          description: errorMessage,
        });

        return;
      }

      accessTokenService.set(accessToken, values.rememberMe);

      if (state.dest) {
        redirectWithToken(state.dest, accessToken);
      }
    } catch (error: any) {
      Notification.error({
        description:
          error?.response?.data?.result?.message ||
          'An unexpected error occurred while logging you in.',
      });

      console.error(error);
    } finally {
      updateState({ loading: false });
      setRefreshReCaptcha((r) => !r);
    }
  };

  const resendEmailVerificationHandler = async (email: string) => {
    const res = await registerService.resendEmailVerification(email);

    Notification[res?.success ? 'success' : 'error']({ description: msg })

    if (res?.success) {
      resetResendTimer();
    }
  };

  useEffect(() => {
    if (get_out === 'true') {
      accessTokenService.remove();

      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('get_out');
      setSearchParams(nextParams, { replace: true });

      window.location.reload();
      return;
    }

    const accessToken = accessTokenService.get();

    if (!accessToken) {
      return;
    }

    if (state.dest) {
      redirectWithToken(state.dest, accessToken);
    }
  }, [state.dest]);

  useEffect(() => {
    if (msg) {
      Notification[error === 'true' ? 'error' : 'success']({ description: msg })
    }
  }, [searchParams]);

  return { loginHandler, captchaVerifyHandler, resendEmailVerificationHandler, refreshReCaptcha, resendTimer, ...state };
};

export default useLogin;
